import React, { useState, useEffect } from 'react';
import { Button } from '@components/Button';
import { Copy, Check, ArrowsClockwise, Warning } from '@phosphor-icons/react';
import { api } from '@services/api';

interface LinkKitButtonProps {
  token_kit: string;
  id_agendamento?: number;
  data_agendamento?: string; // formato 'YYYY-MM-DD' ou 'DD/MM/YYYY'
  onTokenRenovado?: (novoToken: string) => void; // callback para atualizar o token no componente pai
}

/**
 * Verifica se o token do kit está expirado.
 * Regra: válido até 24h após a data do agendamento (dia seguinte).
 */
function isTokenExpirado(data_agendamento?: string): boolean {
  if (!data_agendamento) return false;

  let dataAgenda: Date | null = null;

  if (data_agendamento.includes('-')) {
    // Formato YYYY-MM-DD
    dataAgenda = new Date(data_agendamento + 'T00:00:00');
  } else if (data_agendamento.includes('/')) {
    // Formato DD/MM/YYYY
    const [dia, mes, ano] = data_agendamento.split('/');
    dataAgenda = new Date(`${ano}-${mes}-${dia}T00:00:00`);
  }

  if (!dataAgenda || isNaN(dataAgenda.getTime())) return false;

  // Prazo: data do agendamento + 1 dia (24h)
  const prazoExpiracao = new Date(dataAgenda);
  prazoExpiracao.setDate(prazoExpiracao.getDate() + 1);

  const agora = new Date();
  agora.setHours(0, 0, 0, 0);

  return agora > prazoExpiracao;
}

export const LinkKitButton: React.FC<LinkKitButtonProps> = ({
  token_kit,
  id_agendamento,
  data_agendamento,
  onTokenRenovado
}) => {
  const [copied, setCopied] = useState(false);
  const [renovando, setRenovando] = useState(false);
  const [tokenAtual, setTokenAtual] = useState(token_kit);
  const [isExpired, setIsExpired] = useState(() => isTokenExpirado(data_agendamento));

  useEffect(() => {
    setTokenAtual(token_kit);
    setIsExpired(isTokenExpirado(data_agendamento));
  }, [token_kit, data_agendamento]);

  function handleCopy() {
    if (!tokenAtual) return;
    const url = `${window.location.origin}/kit-externo?token=${tokenAtual}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  async function handleRenovar() {
    if (!id_agendamento || renovando) return;
    setRenovando(true);
    try {
      const res = await api.post('/ravim/renovar-token-kit', {
        id_agendamento
      });
      const novoToken: string = res.data.token_kit;
      setTokenAtual(novoToken);
      setIsExpired(false); // Token is no longer expired after renewal
      if (onTokenRenovado) onTokenRenovado(novoToken);

      // Copiar automaticamente o novo link após renovar
      const url = `${window.location.origin}/kit-externo?token=${novoToken}`;
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      });
    } catch {
      // Silencioso — o operador verá que o link não foi copiado
    } finally {
      setRenovando(false);
    }
  }

  // Sem token: não renderiza nada
  if (!tokenAtual) return null;

  // Link expirado: exibe botão de renovação
  if (isExpired) {
    return (
      <Button
        title={renovando ? 'Gerando novo link...' : 'Gerar novo link do Kit'}
        icon={
          renovando
            ? <ArrowsClockwise size={20} style={{ animation: 'spin 1s linear infinite' }} />
            : <Warning size={20} />
        }
        variantstyle="SECUNDARY"
        onClick={handleRenovar}
        style={{ minWidth: '150px' }}
      />
    );
  }

  // Link válido: exibe botão de copiar
  return (
    <Button
      title={copied ? 'Link copiado!' : 'Copiar Link do Kit'}
      icon={copied ? <Check size={20} /> : <Copy size={20} />}
      variantstyle="SECUNDARY"
      onClick={handleCopy}
      style={{ minWidth: '150px' }}
    />
  );
};
